import { forwardRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'

/**
 * Radiant `Link` translated for React Router.
 *
 * The Tailwind Plus Radiant template wraps `next/link` in Headless UI's
 * `DataInteractive`. We keep the same `href` prop API used throughout the
 * template, but route internal paths through React Router's `Link` and render
 * external / hash / mailto / tel targets as a plain anchor.
 */
type LinkProps = React.ComponentPropsWithoutRef<'a'> & { href: string }

function isExternal(href: string) {
  return (
    /^https?:\/\//i.test(href) ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#')
  )
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, target, rel, ...props },
  ref,
) {
  if (isExternal(href)) {
    const resolvedRel =
      target === '_blank' ? rel ?? 'noopener noreferrer' : rel
    return (
      <a ref={ref} href={href} target={target} rel={resolvedRel} {...props} />
    )
  }

  return <RouterLink ref={ref} to={href} target={target} rel={rel} {...props} />
})
